import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { CalendarView } from '../components/CalendarView';
import { Textarea } from '../components/ui/Textarea';
import { DatePicker } from '../components/ui/DatePicker';
import { Tooltip } from '../components/ui/Tooltip';
import { Plus, Filter, Eye, Edit, Search, Bell, Calendar as CalendarIcon, List, Clock, CheckCircle2, X, Save, Trash2, AlertCircle } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
// Mock data
const initialDeadlinesData = Array.from({
  length: 50
}, (_, i) => {
  const types = ['Riunione annuale', 'Prova di emergenza', 'Tampone alimentare', 'Campionamento alimentare', 'Audit interno', 'Report annuale'];
  const priorities = ['high', 'medium', 'low'];
  const statuses = ['In scadenza', 'Urgente', 'Completata', 'Pianificata'];
  const today = new Date();
  const day = i % 28 + 1;
  const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return {
    id: String(i + 1),
    code: `SCA-2025/${String(i + 1).padStart(3, '0')}`,
    description: i % 3 === 0 ? `Rinnovo DVR ${i}` : `Corso Aggiornamento ${i}`,
    type: types[i % types.length],
    date: dateStr,
    client: `Azienda ${i} SRL`,
    notifyClient: i % 2 === 0 ? 'Sì' : 'No',
    status: statuses[i % statuses.length],
    priority: priorities[i % priorities.length] as 'high' | 'medium' | 'low',
    responsible: i % 4 === 0 ? 'Alessio' : i % 4 === 1 ? 'Lorenzo' : i % 4 === 2 ? 'Gian Franco' : 'Tiziano'
  };
});
const columnsConfig = [{
  key: 'code',
  label: 'Codice'
}, {
  key: 'description',
  label: 'Descrizione'
}, {
  key: 'type',
  label: 'Tipologia'
}, {
  key: 'client',
  label: 'Cliente'
}, {
  key: 'date',
  label: 'Data scadenza'
}, {
  key: 'notifyClient',
  label: 'Notifica'
}, {
  key: 'responsible',
  label: 'Responsabile'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function DeadlinesPage() {
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('calendar');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [recurrenceMode, setRecurrenceMode] = useState<'single' | 'multiple' | 'recurring'>('single');
  const [multipleDates, setMultipleDates] = useState<string[]>(['']);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const {
    addNotification
  } = useNotifications();
  // Custom Tipologia State
  const initialTipologiaOptions = [{
    value: 'Riunione annuale',
    label: 'Riunione annuale'
  }, {
    value: 'Prova di emergenza',
    label: 'Prova di emergenza'
  }, {
    value: 'Tampone alimentare',
    label: 'Tampone alimentare'
  }, {
    value: 'Campionamento alimentare',
    label: 'Campionamento alimentare'
  }, {
    value: 'Audit interno',
    label: 'Audit interno'
  }, {
    value: 'Report annuale',
    label: 'Report annuale'
  }];
  const [tipologiaOptions, setTipologiaOptions] = useState(initialTipologiaOptions);
  const [showCustomTipologiaInput, setShowCustomTipologiaInput] = useState(false);
  const [newTipologia, setNewTipologia] = useState('');
  const handleAddCustomTipologia = () => {
    if (newTipologia.trim()) {
      const newOption = {
        value: newTipologia.trim(),
        label: newTipologia.trim()
      };
      setTipologiaOptions([...tipologiaOptions, newOption]);
      setFormData({
        ...formData,
        type: newTipologia.trim()
      });
      setNewTipologia('');
      setShowCustomTipologiaInput(false);
    } else {
      setShowCustomTipologiaInput(false);
      setFormData({
        ...formData,
        type: ''
      });
    }
  };
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialDeadlinesData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['description', 'client', 'type', 'code']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setSelectedItem(item);
    setIsDeleteConfirmOpen(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setIsDeleteConfirmOpen(false);
      addNotification('success', 'Scadenza eliminata', 'La scadenza è stata eliminata con successo.');
    }, 1500);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    const nextCode = `SCA-2025/${String(initialDeadlinesData.length + 1).padStart(3, '0')}`;
    setFormData({
      code: nextCode,
      notifyClient: false,
      notifyRef: false
    });
    setRecurrenceMode('single');
    setMultipleDates(['']);
    setIsModalOpen(true);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
      addNotification('success', 'Scadenza salvata', 'La scadenza è stata salvata correttamente.');
    }, 2000);
  };
  const handleEventClick = (event: any) => {
    const item = initialDeadlinesData.find(d => d.id === event.id);
    if (item) {
      handleView(item);
    }
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  return <Layout data-id="element-4722" data-mp="boy2m79ii7dd">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-4723" data-mp="boeqx8aggf7s">
        <div data-id="element-4724" data-mp="gpc9hlzdnb9t">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-4725" data-mp="gplzh9kf9ifi">Scadenziario</h1>
          <p className="text-slate-500 mt-1" data-id="element-4726" data-mp="kq5y6hkn8u3r">
            Gestione scadenze tecniche e normative.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-4727" data-mp="un0uquxcwkfe">
          <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-4728" data-mp="ulz6tpv8s3g9">
            <button onClick={() => setViewMode('calendar')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'calendar' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4729" data-mp="zdz9hq2p5agj">
              <CalendarIcon className="h-4 w-4 inline-block mr-1.5" data-id="element-4730" data-mp="1ddi7hq1sn5f4" />{' '}
              Calendario
            </button>
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4731" data-mp="1ynxk32s32vwn">
              <List className="h-4 w-4 inline-block mr-1.5" data-id="element-4732" data-mp="28kaclxbw73pt" /> Lista
            </button>
          </div>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-4734" data-mp="1fy8t1krcrzmk" />} data-id="element-4733" data-mp="1fxz13rmqzie8">
            Nuova scadenza
          </Button>
        </div>
      </div>

      {viewMode === 'list' ? <>
          <Card className="mb-6" noPadding data-id="element-4735" data-mp="1pou0zysx2gi5">
            <div className="p-4 flex flex-col gap-4" data-id="element-4736" data-mp="1ppvdm7pkzb1z">
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-4737" data-mp="1kw0vh8pmj450">
                <div className="flex-1" data-id="element-4738" data-mp="x7hkumj3qf9r">
                  <Input placeholder="Cerca scadenza..." startContent={<Search className="h-4 w-4" data-id="element-4740" data-mp="s7bt76cu0sro" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-4739" data-mp="x66kyxss1rnx" />
                </div>
                <div className="flex gap-2" data-id="element-4741" data-mp="38nrl9l1ejnrn">
                  <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-4743" data-mp="3mlq0strglq8q" />} data-id="element-4742" data-mp="2ty2i5k9r19oq">
                    Filtri{' '}
                    {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-4744" data-mp="3rkjccgn6r769">
                        {Object.keys(filters).filter(k => filters[k]).length}
                      </Badge>}
                  </Button>
                  <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="deadlines" data-id="element-4745" data-mp="226eb9d9qpfda" />
                </div>
              </div>

              {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-4746" data-mp="3fa56bwesj5s9">
                  <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
              value: '',
              label: 'Tutte'
            }, ...tipologiaOptions]} data-id="element-4747" data-mp="31gnyv9zdpjil" />
                  <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
              value: '',
              label: 'Tutti'
            }, {
              value: 'In scadenza',
              label: 'In scadenza'
            }, {
              value: 'Urgente',
              label: 'Urgente'
            }, {
              value: 'Completata',
              label: 'Completata'
            }, {
              value: 'Pianificata',
              label: 'Pianificata'
            }]} data-id="element-4748" data-mp="2mky7ewbfce6h" />
                  <Select label="Responsabile" value={filters.responsible || ''} onChange={e => setFilter('responsible', e.target.value)} options={[{
              value: '',
              label: 'Tutti'
            }, {
              value: 'Alessio',
              label: 'Alessio'
            }, {
              value: 'Lorenzo',
              label: 'Lorenzo'
            }, {
              value: 'Gian Franco',
              label: 'Gian Franco'
            }, {
              value: 'Tiziano',
              label: 'Tiziano'
            }]} data-id="element-4749" data-mp="2xz9p8gt5nq57" />
                  <Select label="Notifica" value={filters.notifyClient || ''} onChange={e => setFilter('notifyClient', e.target.value)} options={[{
              value: '',
              label: 'Tutte'
            }, {
              value: 'Sì',
              label: 'Sì'
            }, {
              value: 'No',
              label: 'No'
            }]} data-id="element-4750" data-mp="3mlat5t1g9z7q" />
                  <div className="col-span-full flex justify-end" data-id="element-4751" data-mp="1kpbq9j6hmtbt">
                    <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-4753" data-mp="2cj40a5wknimh" />} data-id="element-4752" data-mp="1fr0g17ca6gt2">
                      Resetta filtri
                    </Button>
                  </div>
                </div>}
            </div>
          </Card>

          <Card noPadding className="overflow-hidden" data-id="element-4754" data-mp="1yqeeo2kygfpw">
            <div className="overflow-x-auto" data-id="element-4755" data-mp="1yns8a69sw699">
              <table className="w-full text-left border-collapse table-auto" data-id="element-4756" data-mp="2w5ii1vkgzew5">
                <thead data-id="element-4757" data-mp="2retqkg8sihx0">
                  <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-4758" data-mp="2rea54pjxlbe8">
                    {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-4 py-4 ${col.key === 'actions' ? 'text-left pr-6' : ''}`} data-id="element-4759" data-mp="21yw57vhm3t3j">
                          {col.label}
                        </th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-4760" data-mp="2ynbwbw0deair">
                  {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-4761" data-mp="2kwp6yugi1wzc">
                      {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-4762" data-mp="3dk1jscwuyqfg">
                          {item.code}
                        </td>}
                      {visibleColumns.includes('description') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-4763" data-mp="3nqmay9akg8tk">
                          {item.description}
                        </td>}
                      {visibleColumns.includes('type') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-4764" data-mp="etr0x42lldp">
                          {item.type}
                        </td>}
                      {visibleColumns.includes('client') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-4765" data-mp="3iqggklcww87s">
                          {item.client}
                        </td>}
                      {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm font-mono text-slate-600" data-id="element-4766" data-mp="xzpobx7pznu7">
                          {item.date}
                        </td>}
                      {visibleColumns.includes('notifyClient') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-4767" data-mp="1146ly8bihk8w">
                          {item.notifyClient}
                        </td>}
                      {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-4768" data-mp="1f2er33ipbrv5">
                          {item.responsible}
                        </td>}
                      {visibleColumns.includes('status') && <td className="px-4 py-4 whitespace-nowrap" data-id="element-4769" data-mp="mdr0csf6oaa7">
                          <Badge variant={item.status === 'Urgente' ? 'danger' : item.status === 'In scadenza' ? 'warning' : item.status === 'Completata' ? 'success' : 'info'} data-id="element-4770" data-mp="rc0vx44tlcwc">
                            {item.status}
                          </Badge>
                        </td>}
                      {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-4771" data-mp="3dbu1b9ti11vk">
                          <div className="flex items-center gap-1" data-id="element-4772" data-mp="3ddex12rjuqv0">
                            <Tooltip content="Visualizza scadenza" data-id="element-4773" data-mp="39ailfw0ffv0k">
                              <Button variant="light" size="sm" className="!px-2" onClick={() => handleView(item)} data-id="element-4774" data-mp="34ih0zmt3uxzg">
                                <Eye className="h-4 w-4 text-slate-500" data-id="element-4775" data-mp="2uizx9axeyu64" />
                              </Button>
                            </Tooltip>
                            <Tooltip content="Modifica scadenza" data-id="element-4776" data-mp="2ll7hk5ns5ki7">
                              <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(item)} data-id="element-4777" data-mp="dbpwk4sqd9t8">
                                <Edit className="h-4 w-4 text-seform-orange" data-id="element-4778" data-mp="3fmu0xtyv95r" />
                              </Button>
                            </Tooltip>
                            <Tooltip content="Elimina scadenza" data-id="element-4779" data-mp="3qpbdk21rvfq3">
                              <Button variant="light" size="sm" className="!px-2" onClick={() => handleDelete(item)} data-id="element-4780" data-mp="3vgt5y1pqc7zg">
                                <Trash2 className="h-4 w-4 text-red-500" data-id="element-4781" data-mp="3lox54iw9e3rj" />
                              </Button>
                            </Tooltip>
                          </div>
                        </td>}
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="border-t border-slate-100" data-id="element-4782" data-mp="2o6gq4x18pqc8">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-4783" data-mp="2o7rr6p3awjzn" />
            </div>
          </Card>
        </> : <CalendarView events={searchResult.map(item => ({
      id: item.id,
      title: item.description,
      date: item.date,
      type: item.type,
      priority: item.priority,
      client: item.client
    }))} onEventClick={handleEventClick} data-id="element-4784" data-mp="375ckq9h0g6ql" />}

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Dettaglio scadenza" size="lg" data-id="element-4785" data-mp="3ursrp8tgj3yn">
        {selectedItem && <div className="space-y-6" data-id="element-4786" data-mp="6ahdn8yj4lp0">
            <div className="grid grid-cols-2 gap-4" data-id="element-4787" data-mp="19tcvvki0qhl">
              <div data-id="element-4788" data-mp="191ujmfwwp99">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4789" data-mp="1bo23m2sz4rh">
                  Codice
                </span>
                <p className="font-mono font-medium" data-id="element-4790" data-mp="b6xynuafmjxv">{selectedItem.code}</p>
              </div>
              <div data-id="element-4791" data-mp="3jmrjglbsqhkq">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4792" data-mp="3jocc25ejvqfy">
                  Stato
                </span>
                <div className="mt-1" data-id="element-4793" data-mp="3eq127uo99fxq">
                  <Badge variant={selectedItem.status === 'Urgente' ? 'danger' : selectedItem.status === 'In scadenza' ? 'warning' : selectedItem.status === 'Completata' ? 'success' : 'info'} data-id="element-4794" data-mp="3tcubojb3fri0">
                    {selectedItem.status}
                  </Badge>
                </div>
              </div>
              <div className="col-span-full" data-id="element-4795" data-mp="1g75yp1h75m0f">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4796" data-mp="11hi22djynbj1">
                  Descrizione
                </span>
                <p className="font-medium" data-id="element-4797" data-mp="16hmi62gyi48q">{selectedItem.description}</p>
              </div>
              <div data-id="element-4798" data-mp="1u5vzar3863tj">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4799" data-mp="1u7gqc6qbz4mn">
                  Tipologia
                </span>
                <p className="font-medium" data-id="element-4800" data-mp="1z2w5r33zl8l6">{selectedItem.type}</p>
              </div>
              <div data-id="element-4801" data-mp="1l57glj2vzfab">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4802" data-mp="1p72jgs0gu0q7">
                  Cliente
                </span>
                <p className="font-medium" data-id="element-4803" data-mp="9q2eohakxkrr">{selectedItem.client}</p>
              </div>
              <div data-id="element-4804" data-mp="dsf7y6853q7n">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4805" data-mp="dsyq9o1pcgm7">
                  Data scadenza
                </span>
                <p className="font-mono font-medium" data-id="element-4806" data-mp="2ggnke5xk5aw5">{selectedItem.date}</p>
              </div>
              <div data-id="element-4807" data-mp="2bg9kjb641797">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4808" data-mp="2bhu8ghxx5ry3">
                  Responsabile
                </span>
                <p className="font-medium" data-id="element-4809" data-mp="21qg9qcdskw60">{selectedItem.responsible}</p>
              </div>
              <div data-id="element-4810" data-mp="1xomggp5xeufv">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4811" data-mp="1xour0aektnnw">
                  Notifica cliente
                </span>
                <p className="font-medium" data-id="element-4812" data-mp="1srcrfamg8ksk">{selectedItem.notifyClient}</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-4813" data-mp="1iqu05usk220m">
              <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-4814" data-mp="1iswwewkawrds">
                Chiudi
              </Button>
              <Button onClick={() => {
            setIsViewModalOpen(false);
            handleEdit(selectedItem);
          }} startContent={<Edit className="h-4 w-4" data-id="element-4816" data-mp="3plmiw3qa1azv" />} data-id="element-4815" data-mp="3d30olvagmfav">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica scadenza' : 'Nuova scadenza'} size="lg" data-id="element-4817" data-mp="37cy04lk2l3u">
        <AnimatePresence mode="wait" data-id="element-4818" data-mp="36pcvgpthj837">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-16" data-id="element-4819" data-mp="3bn4zujbbgmoa">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-4820" data-mp="24sp234vsqup8">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-4821" data-mp="2it9jbwm2cigm" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-4822" data-mp="2wkn1b64bp89j">
                Scadenza salvata con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-4823" data-mp="2sjuqi8fsyvb5">
              <div className="space-y-6" data-id="element-4824" data-mp="31lkhxe9opu8x">
                <section data-id="element-4825" data-mp="3l4zv5ws9qqxk">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4826" data-mp="3l6kmzen77vrq">
                    <Clock className="h-4 w-4" data-id="element-4827" data-mp="3q3umjn4axnta" /> Dati base
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4828" data-mp="3b6k3nspbxfnj">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} data-id="element-4829" data-mp="3g5na9hkyjtxm" />
                    <div data-id="element-4830" data-mp="2serb1ze69wgc">
                      <Select label="Tipologia" value={formData.type || ''} onChange={e => {
                    if (e.target.value === '__custom__') {
                      setShowCustomTipologiaInput(true);
                    } else {
                      setFormData({
                        ...formData,
                        type: e.target.value
                      });
                      setShowCustomTipologiaInput(false);
                    }
                  }} options={[{
                    value: '',
                    label: 'Seleziona...'
                  }, ...tipologiaOptions, {
                    value: '__custom__',
                    label: '+ Aggiungi nuova tipologia...'
                  }]} required data-id="element-4831" data-mp="2xfp1t0d8zytg" />
                      {showCustomTipologiaInput && <div className="mt-2 animate-in fade-in slide-in-from-top-2" data-id="element-4832" data-mp="3nracdd0o8sr2">
                          <Input autoFocus placeholder="Inserisci nuova tipologia" value={newTipologia} onChange={e => setNewTipologia(e.target.value)} onBlur={handleAddCustomTipologia} onKeyDown={e => e.key === 'Enter' && handleAddCustomTipologia()} data-id="element-4833" data-mp="3jnkn0lc6a74z" />
                        </div>}
                    </div>
                    <div className="col-span-full" data-id="element-4834" data-mp="2muuu7wp5ugml">
                      <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} required data-id="element-4835" data-mp="2qxhoe49b0dmy" />
                    </div>
                    <Select label="Cliente" value={formData.client || ''} onChange={e => setFormData({
                  ...formData,
                  client: e.target.value
                })} options={[{
                  value: 'Azienda 0 SRL',
                  label: 'Azienda 0 SRL'
                }, {
                  value: 'Azienda 1 SRL',
                  label: 'Azienda 1 SRL'
                }, {
                  value: 'Azienda 2 SRL',
                  label: 'Azienda 2 SRL'
                }]} required data-id="element-4836" data-mp="33esfof3i25lw" />
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-4837" data-mp="4pbkhuot4rc1" />
                  </div>
                </section>

                <section data-id="element-4838" data-mp="1imeeurf403mn">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4839" data-mp="26bp87nu4qvqr">
                    <CalendarIcon className="h-4 w-4" data-id="element-4840" data-mp="2adk9isc2994j" /> Date e ripetizioni
                  </h4>
                  <div className="flex gap-4 mb-4" data-id="element-4841" data-mp="1wmp984fmo0ck">
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-id="element-4842" data-mp="1wjta4cs1n5mj">
                      <input type="radio" name="recurrence" checked={recurrenceMode === 'single'} onChange={() => setRecurrenceMode('single')} className="text-seform-orange focus:ring-seform-orange" data-id="element-4843" data-mp="21j5yj1ykckr8" />
                      Data singola
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-id="element-4844" data-mp="4swntwcw998f">
                      <input type="radio" name="recurrence" checked={recurrenceMode === 'multiple'} onChange={() => setRecurrenceMode('multiple')} className="text-seform-orange focus:ring-seform-orange" data-id="element-4845" data-mp="9m98xwid6xwf" />
                      Date multiple
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-id="element-4846" data-mp="3m58vpizt60jy">
                      <input type="radio" name="recurrence" checked={recurrenceMode === 'recurring'} onChange={() => setRecurrenceMode('recurring')} className="text-seform-orange focus:ring-seform-orange" data-id="element-4847" data-mp="3r3sfpduud29o" />
                      Periodo ripetuto
                    </label>
                  </div>

                  {recurrenceMode === 'single' && <DatePicker label="Data scadenza" value={formData.date || ''} onChange={e => setFormData({
                ...formData,
                date: e.target.value
              })} data-id="element-4848" data-mp="16okk1aqbc608" />}

                  {recurrenceMode === 'multiple' && <div className="space-y-3" data-id="element-4849" data-mp="jpo37rh7ca49">
                      <p className="text-xs text-slate-500" data-id="element-4850" data-mp="jqzgqj0g8w8k">
                        Aggiungi più date per questa scadenza
                      </p>
                      {multipleDates.map((date, index) => <div key={index} className="flex gap-2 items-end" data-id="element-4851" data-mp="2e473roi6o3jn" data-mpi={index}>
                          <div className="flex-1" data-id="element-4852" data-mp="bfz46pdv6dck">
                            <DatePicker label={index === 0 ? 'Data scadenza' : `Data scadenza ${index + 1}`} value={date} onChange={e => {
                      const updated = [...multipleDates];
                      updated[index] = e.target.value;
                      setMultipleDates(updated);
                    }} data-id="element-4853" data-mp="bf7697wso3j4" />
                          </div>
                          {index > 0 && <Button size="sm" variant="flat" color="danger" className="mb-0.5" onClick={() => {
                    setMultipleDates(multipleDates.filter((_, i) => i !== index));
                  }} data-id="element-4854" data-mp="q80zdw4zoi69">
                              <X className="h-4 w-4" data-id="element-4855" data-mp="3ccj9r6k1ji2g" />
                            </Button>}
                        </div>)}
                      <Button size="sm" variant="flat" startContent={<Plus className="h-4 w-4" data-id="element-4857" data-mp="2er57mlfnxt2f" />} onClick={() => setMultipleDates([...multipleDates, ''])} data-id="element-4856" data-mp="2jpoque2vgqr3">
                        Aggiungi data
                      </Button>
                    </div>}

                  {recurrenceMode === 'recurring' && <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-4858" data-mp="20vt17gs5ae5o">
                      <DatePicker label="Data inizio" data-id="element-4859" data-mp="z7xrvgr7rhcx" />
                      <div className="flex gap-2 items-end" data-id="element-4860" data-mp="z98wfishgt53">
                        <Input label="Ripeti ogni" type="number" defaultValue="1" data-id="element-4861" data-mp="145za5zgtfcnc" />
                        <Select options={[{
                    value: 'M',
                    label: 'Mesi'
                  }, {
                    value: 'Y',
                    label: 'Anni'
                  }]} defaultValue="Y" data-id="element-4862" data-mp="1cyu8sabs2a45" />
                      </div>
                      <Input label="Per (volte)" type="number" defaultValue="5" data-id="element-4863" data-mp="3tnpyxrr7sd2w" />
                      <div className="col-span-full pt-2" data-id="element-4864" data-mp="1m04b4xz8lu4">
                        <p className="text-xs font-medium text-slate-600 mb-1" data-id="element-4865" data-mp="3q6u153k4z6ij">
                          Anteprima occorrenze:
                        </p>
                        <div className="flex gap-2 flex-wrap" data-id="element-4866" data-mp="3gfz4d599koiu">
                          <Badge variant="neutral" data-id="element-4867" data-mp="3ggiq6x21bx25">10/05/2024</Badge>
                          <Badge variant="neutral" data-id="element-4868" data-mp="3cf6x2hjyctp7">10/05/2025</Badge>
                          <Badge variant="neutral" data-id="element-4869" data-mp="czaaojus59al">10/05/2026</Badge>
                        </div>
                      </div>
                    </div>}
                </section>

                <section data-id="element-4870" data-mp="3ykdbbpvfaur">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4871" data-mp="3xsvd3p75bmy">
                    <Bell className="h-4 w-4" data-id="element-4872" data-mp="3v43s4nn6bq8h" /> Notifiche
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4873" data-mp="2op30gu56lu7p">
                    <div className="flex items-center gap-2" data-id="element-4874" data-mp="2jpzun7hdnjyo">
                      <input type="checkbox" id="notifyClient" className="rounded text-seform-orange focus:ring-seform-orange" checked={formData.notifyClient || false} onChange={e => setFormData({
                    ...formData,
                    notifyClient: e.target.checked
                  })} data-id="element-4875" data-mp="2jof4du23in6m" />
                      <label htmlFor="notifyClient" className="text-sm text-slate-700" data-id="element-4876" data-mp="2774arckfgj4i">
                        Invia notifica al cliente
                      </label>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-4877" data-mp="1xhkpy1d5mf70">
                      <input type="checkbox" id="notifyRef" className="rounded text-seform-orange focus:ring-seform-orange" checked={formData.notifyRef || false} onChange={e => setFormData({
                    ...formData,
                    notifyRef: e.target.checked
                  })} data-id="element-4878" data-mp="1jh9zgzfrb2md" />
                      <label htmlFor="notifyRef" className="text-sm text-slate-700" data-id="element-4879" data-mp="19t4nh2fqy9ag">
                        Invia notifica referente SEF
                      </label>
                    </div>
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-4880" data-mp="1xiz0pl3z9zv8">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-4881" data-mp="1slqdh63z35n4">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-4883" data-mp="38x1ct9c3ljkd" />} data-id="element-4882" data-mp="21j0t13ntpgxu">
                    Salva scadenza
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Conferma eliminazione" size="sm" data-id="element-4884" data-mp="3mw0zibg1vodw">
        <AnimatePresence mode="wait" data-id="element-4885" data-mp="2u855jehv8akt">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-4886" data-mp="2u6agbnzk9wax">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-4887" data-mp="23ymf558wyfk0">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-4888" data-mp="1l1398ahr5d1t" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-4889" data-mp="1g7qvivem2qnu">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-4890" data-mp="16bx8uj76hgsc">
              <div className="space-y-6" data-id="element-4891" data-mp="xb8vzrtitmh1">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-4892" data-mp="xcafmkolrhad">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-4893" data-mp="2q998rppccjc8" />
                  <p className="text-sm text-red-700" data-id="element-4894" data-mp="2levbtiv1645g">
                    Attenzione: questa azione è irreversibile. La scadenza verrà
                    eliminata definitivamente.
                  </p>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-4895" data-mp="2cdx3iodk7ah2">
                  <Button variant="flat" color="default" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-4896" data-mp="394pnvy7izoon">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} data-id="element-4897" data-mp="3075kojq3ribe">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}